import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts";
import { MessageCircle, TrendingUp, Hash, AtSign } from "lucide-react";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Tooltip } from "./Tooltip";

export const InfluencerEngagement = ({ userDatas }) => {
  const yAxisTickFormatter = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value;
  };

  const barColors = ["#E5E7EB"];

  const rawData = userDatas?.audience_engagement_rate_histogram || [];

  const chartData = rawData.map((item, index) => ({
    ...item,
    id: `bar-${index}`,
    position: index + 1,
  }));

  return (
    <section className="space-y-6 px-4 sm:px-6 mb-8 w-full max-w-full overflow-x-hidden">
      {/* Engagement Chart */}
      <div className="bg-white hello rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300 w-full">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 w-full">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-gray-900 flex gap-2">
                Engagement Analytics{" "}
                <Tooltip description="Engagement Over Time - Displays the number of likes and comments an account receives across posts—helpful for tracking engagement trends, audience interaction, and growth over time." />
              </h2>
              <p className="text-sm text-gray-600">
                Performance metrics for recent content
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto justify-center">
            <div className="flex items-center text-gray-600">
              <FavoriteIcon
                className="w-4 h-4 mr-2"
                sx={{ color: "#EF4444" }}
              />
              <span className="text-sm font-medium">Likes</span>
            </div>
            <div className="flex items-center text-gray-600">
              <MessageCircle className="w-4 h-4 mr-2 text-blue-500" />
              <span className="text-sm font-medium">Comments</span>
            </div>
          </div>
        </div>

        <div className="h-64 flex justify-center w-full">
          <div className="w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
              >
                <CartesianGrid vertical={false} stroke="#e5e7eb" />
                <XAxis
                  dataKey="id"
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                />
                <YAxis
                  tickFormatter={yAxisTickFormatter}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#6b7280", fontSize: 12 }}
                />
                <RechartsTooltip
                  formatter={(value) => [yAxisTickFormatter(value), "Total"]}
                  labelFormatter={(label) => {
                    const item = chartData.find((d) => d.id === label);
                    return item
                      ? `Engagement Range: ${item.min?.toFixed(4) || "N/A"}`
                      : "";
                  }}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "2px solid #10b981",
                    borderRadius: "8px",
                    color: "#1f2937",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="total" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={entry.id}
                      fill={barColors[index % barColors.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Hashtags and Mentions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
        {/* Hashtags Card */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300 w-full">
          <div className="flex items-center gap-3 mb-6 w-full">
            <div className="p-3 bg-green-100 rounded-xl flex-shrink-0">
              <Hash className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900 flex gap-2">
                Popular Hashtags{" "}
                <Tooltip description="Most frequently used #hashtags on the last 30 posts." />
              </h3>
              <p className="text-sm text-gray-600 truncate">
                {userDatas?.top_hashtag?.length || 0} trending tags
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 w-full">
            {userDatas?.top_hashtag?.map((hashtag, index) => (
              <span
                key={`hashtag-${index}-${hashtag.tag}`}
                className="bg-[#F3F4F6] text-black px-3 py-2 rounded-xl text-sm font-medium cursor-pointer flex-shrink-0"
              >
                #{hashtag.tag}
              </span>
            ))}
          </div>
        </div>

        {/* Mentions Card */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300 w-full">
          <div className="flex items-center gap-3 mb-6 w-full">
            <div className="p-3 bg-emerald-100 rounded-xl flex-shrink-0">
              <AtSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-gray-900 flex gap-2">
                Popular Mentions{" "}
                <Tooltip description="Most frequently used @mentions on the last 30 posts." />
              </h3>
              <p className="text-sm text-gray-600 truncate">
                {userDatas?.top_mention?.length || 0} frequent mentions
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 w-full">
            {userDatas?.top_mention?.map((mention, index) => (
              <span
                key={`mention-${index}-${mention?.tag}`}
                className="bg-[#F3F4F6] text-black px-3 py-2 rounded-xl text-sm font-medium cursor-pointer flex-shrink-0"
              >
                @{mention?.tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
